import { PostMatchSummary } from './PostMatchSummary';
import { useGameStore } from '../state/useGameStore';
import { disconnectGameServer } from '../game/network';
import { useEffect, useState, type CSSProperties } from 'react';
import { loadLocalProfile, updateLocalProfileFromMatch } from './playerProfile';

const postMatchKeyframes = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes bannerReveal {
    0% { transform: translateX(-50%) scale(2); opacity: 0; }
    60% { transform: translateX(-50%) scale(0.92); opacity: 1; }
    80% { transform: translateX(-50%) scale(1.04); }
    100% { transform: translateX(-50%) scale(1); opacity: 1; }
  }
  @keyframes bannerFlash {
    0% { opacity: 0.6; }
    100% { opacity: 0; }
  }
`;

const buttonStyle: CSSProperties = {
  marginTop: 20,
  minWidth: 240,
  padding: '14px 28px',
  fontSize: 14,
  letterSpacing: 3,
  textTransform: 'uppercase',
  fontFamily: 'system-ui, sans-serif',
  fontWeight: 700,
  color: '#62ddff',
  background: 'rgba(98,221,255,0.08)',
  border: '1px solid rgba(98,221,255,0.35)',
  cursor: 'pointer',
  transition: 'box-shadow 0.2s ease',
  position: 'relative',
  zIndex: 2,
};

export function PostMatchOverlay() {
  const [careerProfile, setCareerProfile] = useState(() => loadLocalProfile());
  const state = useGameStore.getState();
  const allPlayers = state.scoreboardPlayers.length
    ? state.scoreboardPlayers.map((p) => ({
        id: p.id,
        displayName: p.displayName ?? 'Player',
        team: p.team,
        score: p.score ?? 0,
        kills: p.kills ?? 0,
        deaths: p.deaths ?? 0,
        captures: p.captures ?? 0,
        returns: p.returns ?? 0,
      }))
    : [];
  const localPlayer = allPlayers.find((player) => player.id === state.localId) ?? null;
  const matchId = `${state.mapId}:${state.match.winnerTeam ?? 'draw'}:${state.match.teamScores?.red ?? 0}-${state.match.teamScores?.blue ?? 0}:${allPlayers
    .map((player) => `${player.id}:${player.score}:${player.kills}:${player.deaths}:${player.captures}:${player.returns}`)
    .sort()
    .join('|')}`;

  useEffect(() => {
    if (!localPlayer) {
      setCareerProfile(loadLocalProfile());
      return;
    }

    setCareerProfile(
      updateLocalProfileFromMatch({
        matchId,
        mapId: state.mapId,
        winnerTeam: state.match.winnerTeam ?? null,
        localPlayer,
        awards: state.match.awards ?? [],
      }),
    );
  }, [
    localPlayer?.captures,
    localPlayer?.deaths,
    localPlayer?.id,
    localPlayer?.kills,
    localPlayer?.returns,
    localPlayer?.team,
    matchId,
    state.mapId,
    state.match.awards,
    state.match.winnerTeam,
  ]);

  const winnerTeam = state.match.winnerTeam;
  const bannerColor = winnerTeam === 'red' ? '#ff4444' : winnerTeam === 'blue' ? '#4488ff' : '#888888';
  const bannerText = winnerTeam === 'red' ? 'RED VICTORY' : winnerTeam === 'blue' ? 'BLUE VICTORY' : 'DRAW';

  // Phase 160 — pick top scorer (most kills, then most caps, then highest
  // total score) and highlight them as MVP at the top of the panel.
  const mvp = allPlayers.length
    ? allPlayers.reduce((best, p) => {
        if (!best) return p;
        if (p.kills !== best.kills) return p.kills > best.kills ? p : best;
        if (p.captures !== best.captures) return p.captures > best.captures ? p : best;
        return p.score > best.score ? p : best;
      })
    : null;
  const mvpColor = mvp ? (mvp.team === 'red' ? '#ff5b5b' : mvp.team === 'blue' ? '#62ddff' : '#ffd86b') : '#ffd86b';

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 120,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(2,4,8,0.72)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'fadeIn 0.4s ease-out',
      }}
    >
      <style>{postMatchKeyframes}</style>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: bannerColor,
          animation: 'bannerFlash 0.8s ease-out forwards',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '6%',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2,
          animation: 'bannerReveal 0.7s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        }}
      >
        <div
          style={{
            fontSize: 36,
            fontWeight: 900,
            letterSpacing: 6,
            textTransform: 'uppercase',
            color: bannerColor,
            textShadow: `0 0 30px ${bannerColor}80, 0 0 60px ${bannerColor}40`,
            fontFamily: 'system-ui, sans-serif',
            whiteSpace: 'nowrap',
          }}
        >
          {bannerText}
        </div>
      </div>
      {/* Phase 160 — MVP highlight card */}
      {mvp ? (
        <div
          style={{
            position: 'relative',
            zIndex: 3,
            marginTop: '8%',
            marginBottom: 12,
            padding: '10px 22px',
            background: 'rgba(2,8,14,0.7)',
            border: `1px solid ${mvpColor}88`,
            borderRadius: 6,
            boxShadow: `0 0 28px ${mvpColor}44`,
            textAlign: 'center',
            fontFamily: 'monospace',
            color: '#fff',
            animation: 'fadeIn 0.7s ease-out 0.2s both',
          }}
        >
          <div style={{ color: mvpColor, fontSize: 10, letterSpacing: 4, marginBottom: 4 }}>MATCH MVP</div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 2 }}>{mvp.displayName}</div>
          <div style={{ marginTop: 4, fontSize: 11, color: 'rgba(255,255,255,0.78)' }}>
            {mvp.kills} K · {mvp.deaths} D · {mvp.captures} CAP · {mvp.returns} RET · {mvp.score} PTS
          </div>
        </div>
      ) : null}
      <PostMatchSummary
        visible
        winnerTeam={winnerTeam ?? null}
        teamScores={state.match.teamScores ?? {}}
        players={allPlayers}
        countdownSeconds={state.match.countdownSeconds ?? 0}
        localPlayerId={state.localId ?? undefined}
        objectiveTelemetry={state.ctfMacro?.objectiveTelemetry}
        awards={state.match.awards ?? []}
        careerProfile={careerProfile}
      />
      {/* Phase 167 — Play Again + Return to Menu pair. */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button
          type="button"
          style={{
            ...buttonStyle,
            color: '#07111d',
            background: '#62ddff',
            border: '1px solid #62ddff',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 0 18px #62ddff90';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = 'none';
          }}
          onClick={() => {
            const store = useGameStore.getState();
            store.resetMatch();
            // Re-launch standalone session immediately for a fast rematch.
            store.startStandaloneSession();
            store.setGamePhase('playing');
          }}
        >
          PLAY AGAIN
        </button>
        <button
          type="button"
          style={buttonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 0 16px #62ddff40';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = 'none';
          }}
          onClick={() => {
            disconnectGameServer();
            useGameStore.getState().setGamePhase('menu');
          }}
        >
          RETURN TO MENU
        </button>
      </div>
    </div>
  );
}
